import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { SERVER_URL } from '../databucket';

const API = `${SERVER_URL}/api/bulk-outreach`;
// Auth: cookies don't work on localhost (secure:true), so send token via header
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// ── Campaign Thunks ──────────────────────────────────────────────────────────

export const fetchCampaigns = createAsyncThunk('bulkOutreach/fetchCampaigns',
  async ({ page = 1, limit = 20, status, search } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      const { data } = await axios.get(`${API}/campaigns?${params}`, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch campaigns'); }
  }
);

export const fetchCampaign = createAsyncThunk('bulkOutreach/fetchCampaign',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/campaigns/${id}`, auth());
      return data.campaign;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch campaign'); }
  }
);

export const createCampaign = createAsyncThunk('bulkOutreach/createCampaign',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/campaigns`, payload, auth());
      return data.campaign;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to create campaign'); }
  }
);

export const updateCampaign = createAsyncThunk('bulkOutreach/updateCampaign',
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API}/campaigns/${id}`, payload, auth());
      return data.campaign;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to update campaign'); }
  }
);

export const changeCampaignStatus = createAsyncThunk('bulkOutreach/changeCampaignStatus',
  async ({ id, status, scheduledAt }, { rejectWithValue }) => {
    try {
      const { data } = await axios.patch(`${API}/campaigns/${id}/status`, { status, scheduledAt }, auth());
      return data.campaign;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to change status'); }
  }
);

export const deleteCampaign = createAsyncThunk('bulkOutreach/deleteCampaign',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API}/campaigns/${id}`, auth());
      return id;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to delete'); }
  }
);

// ── Recipient Thunks ─────────────────────────────────────────────────────────

export const addRecipients = createAsyncThunk('bulkOutreach/addRecipients',
  async ({ campaignId, contacts, replace }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/campaigns/${campaignId}/recipients`, { contacts, replace }, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to add recipients'); }
  }
);

export const fetchRecipients = createAsyncThunk('bulkOutreach/fetchRecipients',
  async ({ campaignId, page = 1, limit = 50, status, search, step } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      if (step) params.append('step', step);
      const { data } = await axios.get(`${API}/campaigns/${campaignId}/recipients?${params}`, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch recipients'); }
  }
);

export const updateRecipient = createAsyncThunk('bulkOutreach/updateRecipient',
  async ({ campaignId, recipientId, ...payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API}/campaigns/${campaignId}/recipients/${recipientId}`, payload, auth());
      return data.recipient;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to update recipient'); }
  }
);

export const removeRecipients = createAsyncThunk('bulkOutreach/removeRecipients',
  async ({ campaignId, recipientIds }, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API}/campaigns/${campaignId}/recipients`, { ...auth(), data: { recipientIds } });
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to remove recipients'); }
  }
);

export const changeRecipientStatus = createAsyncThunk('bulkOutreach/changeRecipientStatus',
  async ({ campaignId, recipientId, status }, { rejectWithValue }) => {
    try {
      const { data } = await axios.patch(`${API}/campaigns/${campaignId}/recipients/${recipientId}/status`, { status }, auth());
      return data.recipient;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to change recipient status'); }
  }
);

export const autoCleanCampaign = createAsyncThunk('bulkOutreach/autoCleanCampaign',
  async (campaignId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/campaigns/${campaignId}/auto-clean`, {}, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to auto-clean campaign'); }
  }
);

// ── Verification Thunks ──────────────────────────────────────────────────────

export const verifyEmails = createAsyncThunk('bulkOutreach/verifyEmails',
  async ({ campaignId, useZeroBounce = false }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/campaigns/${campaignId}/verify-emails`, { useZeroBounce }, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Verification failed'); }
  }
);

export const checkHistory = createAsyncThunk('bulkOutreach/checkHistory',
  async (campaignId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/campaigns/${campaignId}/check-history`, {}, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'History check failed'); }
  }
);

export const checkDuplicates = createAsyncThunk('bulkOutreach/checkDuplicates',
  async (campaignId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/campaigns/${campaignId}/check-duplicates`, {}, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Duplicate check failed'); }
  }
);

export const checkSpamScore = createAsyncThunk('bulkOutreach/checkSpamScore',
  async ({ subject, body }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/spam-check`, { subject, body }, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Spam check failed'); }
  }
);

// ── Analytics Thunks ─────────────────────────────────────────────────────────

export const fetchAnalytics = createAsyncThunk('bulkOutreach/fetchAnalytics',
  async (campaignId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/campaigns/${campaignId}/analytics`, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch analytics'); }
  }
);

export const fetchLiveFeed = createAsyncThunk('bulkOutreach/fetchLiveFeed',
  async ({ campaignId, limit = 50 }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/campaigns/${campaignId}/live-feed?limit=${limit}`, auth());
      return data.events;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch live feed'); }
  }
);

export const fetchActivityLog = createAsyncThunk('bulkOutreach/fetchActivityLog',
  async ({ campaignId, page = 1, limit = 50 }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/campaigns/${campaignId}/activity-log?page=${page}&limit=${limit}`, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch activity log'); }
  }
);

export const fetchInsights = createAsyncThunk('bulkOutreach/fetchInsights',
  async (campaignId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/campaigns/${campaignId}/insights`, auth());
      return data.insights;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch insights'); }
  }
);

export const compareCampaigns = createAsyncThunk('bulkOutreach/compareCampaigns',
  async (campaignIds, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/compare`, { campaignIds }, auth());
      return data.campaigns;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to compare'); }
  }
);

export const fetchSendTimePrediction = createAsyncThunk('bulkOutreach/fetchSendTimePrediction',
  async (timezone = 'Eastern Time', { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/analytics/send-time?timezone=${encodeURIComponent(timezone)}`, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch send time data'); }
  }
);

// ── Segment Thunks ───────────────────────────────────────────────────────────

export const fetchSegments = createAsyncThunk('bulkOutreach/fetchSegments',
  async ({ page = 1, limit = 20, search } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      const { data } = await axios.get(`${API}/segments?${params}`, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch segments'); }
  }
);

export const createSegment = createAsyncThunk('bulkOutreach/createSegment',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/segments`, payload, auth());
      return data.segment;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to create segment'); }
  }
);

export const updateSegment = createAsyncThunk('bulkOutreach/updateSegment',
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API}/segments/${id}`, payload, auth());
      return data.segment;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to update segment'); }
  }
);

export const deleteSegment = createAsyncThunk('bulkOutreach/deleteSegment',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API}/segments/${id}`, auth());
      return id;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to delete segment'); }
  }
);

// ── Playbook Thunks ──────────────────────────────────────────────────────────

export const fetchPlaybooks = createAsyncThunk('bulkOutreach/fetchPlaybooks',
  async ({ page = 1, limit = 20, search, category } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      const { data } = await axios.get(`${API}/playbooks?${params}`, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch playbooks'); }
  }
);

export const createPlaybook = createAsyncThunk('bulkOutreach/createPlaybook',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/playbooks`, payload, auth());
      return data.playbook;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to create playbook'); }
  }
);

export const updatePlaybook = createAsyncThunk('bulkOutreach/updatePlaybook',
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API}/playbooks/${id}`, payload, auth());
      return data.playbook;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to update playbook'); }
  }
);

export const deletePlaybook = createAsyncThunk('bulkOutreach/deletePlaybook',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API}/playbooks/${id}`, auth());
      return id;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to delete playbook'); }
  }
);

// ── Lead Contact Stats (server-side aggregation) ────────────────────────────

export const selectAllMatching = createAsyncThunk('bulkOutreach/selectAllMatching',
  async ({ leadFilters, contactFilters, countOnly = false }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/select-all-matching`, { leadFilters, contactFilters, countOnly }, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to select all'); }
  }
);

export const fetchLeadContactStats = createAsyncThunk('bulkOutreach/fetchLeadContactStats',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/lead-contact-stats`, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch stats'); }
  }
);

// ── Domain Health + Send Limits ──────────────────────────────────────────────

export const fetchDomainHealth = createAsyncThunk('bulkOutreach/fetchDomainHealth',
  async (days = 7, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/domain-health?days=${days}`, auth());
      return data.domains;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch domain health'); }
  }
);

export const fetchSendLimits = createAsyncThunk('bulkOutreach/fetchSendLimits',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/send-limits`, auth());
      return data.limits;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch send limits'); }
  }
);

export const updateSendLimit = createAsyncThunk('bulkOutreach/updateSendLimit',
  async ({ userId, dailyLimit, weeklyLimit }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API}/send-limits/${userId}`, { dailyLimit, weeklyLimit }, auth());
      return data.limit;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to update send limit'); }
  }
);

// ── Manual Unsubscribe ───────────────────────────────────────────────────────

export const manualUnsubscribe = createAsyncThunk('bulkOutreach/manualUnsubscribe',
  async ({ contactEmail, leadId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/unsubscribe-contact`, { contactEmail, leadId }, auth());
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to unsubscribe'); }
  }
);

export const fetchContactCampaignStatus = createAsyncThunk('bulkOutreach/fetchContactCampaignStatus',
  async (email, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/contact-campaign-status/${encodeURIComponent(email, auth())}`);
      return data;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to fetch status'); }
  }
);

// ── Warm-up Projection ──────────────────────────────────────────────────────

export const fetchWarmUpProjection = createAsyncThunk('bulkOutreach/fetchWarmUpProjection',
  async ({ startVolume, maxVolume, rampFactor, days }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/warm-up-projection`, { startVolume, maxVolume, rampFactor, days }, auth());
      return data.projection;
    } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to get projection'); }
  }
);

// ═════════════════════════════════════════════════════════════════════════════
// SLICE
// ═════════════════════════════════════════════════════════════════════════════

const bulkOutreachSlice = createSlice({
  name: 'bulkOutreach',
  initialState: {
    // Campaigns
    campaigns: [],
    campaignsTotal: 0,
    campaignStats: {},
    currentCampaign: null,
    // Recipients
    recipients: [],
    recipientsTotal: 0,
    // Verification
    verificationResults: null,
    historyResults: null,
    duplicateResults: null,
    spamScore: null,
    // Analytics
    analytics: null,
    liveFeed: [],
    activityLog: [],
    activityLogTotal: 0,
    insights: [],
    comparison: [],
    sendTimePrediction: null,
    // Segments
    segments: [],
    segmentsTotal: 0,
    // Playbooks
    playbooks: [],
    playbooksTotal: 0,
    // Domain Health
    domainHealth: [],
    // Send Limits
    sendLimits: [],
    // Warm-up
    warmUpProjection: [],
    leadContactStats: { leads: 0, contacts: 0, optedIn: 0, verified: 0 },
    // UI state
    loading: false,
    error: null,
    wizardStep: 0,
    wizardDraft: null,
  },
  reducers: {
    clearError: (state) => { state.error = null; },
    clearCurrentCampaign: (state) => { state.currentCampaign = null; state.analytics = null; state.liveFeed = []; state.insights = []; },
    setWizardStep: (state, action) => { state.wizardStep = action.payload; },
    setWizardDraft: (state, action) => { state.wizardDraft = { ...state.wizardDraft, ...action.payload }; },
    clearWizardDraft: (state) => {
      state.wizardDraft = null;
      state.wizardStep = 0;
      // Clear stale verification results so a different campaign doesn't show old issue counts
      state.verificationResults = null;
      state.historyResults = null;
      state.duplicateResults = null;
      state.spamScore = null;
    },
    appendLiveFeedEvent: (state, action) => {
      state.liveFeed = [action.payload, ...state.liveFeed].slice(0, 100);
    },
    clearVerificationResults: (state) => {
      state.verificationResults = null;
      state.historyResults = null;
      state.duplicateResults = null;
    },
    updateCampaignStatus: (state, action) => {
      const { campaignId, status } = action.payload;
      const idx = state.campaigns.findIndex(c => c._id === campaignId);
      if (idx >= 0) state.campaigns[idx].status = status;
      if (state.currentCampaign?._id === campaignId) state.currentCampaign.status = status;
    },
  },
  extraReducers: (builder) => {
    // Generic loading/error pattern
    const pending = (state) => { state.loading = true; state.error = null; };
    const rejected = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      // Campaigns
      .addCase(fetchCampaigns.pending, pending)
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload.campaigns;
        state.campaignsTotal = action.payload.total;
        state.campaignStats = action.payload.stats;
      })
      .addCase(fetchCampaigns.rejected, rejected)

      .addCase(fetchCampaign.pending, pending)
      .addCase(fetchCampaign.fulfilled, (state, action) => { state.loading = false; state.currentCampaign = action.payload; })
      .addCase(fetchCampaign.rejected, rejected)

      .addCase(createCampaign.pending, pending)
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns.unshift(action.payload);
        state.currentCampaign = action.payload;
      })
      .addCase(createCampaign.rejected, rejected)

      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCampaign = action.payload;
        const idx = state.campaigns.findIndex(c => c._id === action.payload._id);
        if (idx >= 0) state.campaigns[idx] = action.payload;
      })

      .addCase(changeCampaignStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCampaign = action.payload;
        const idx = state.campaigns.findIndex(c => c._id === action.payload._id);
        if (idx >= 0) state.campaigns[idx] = action.payload;
      })

      .addCase(deleteCampaign.fulfilled, (state, action) => {
        state.campaigns = state.campaigns.filter(c => c._id !== action.payload);
        if (state.currentCampaign?._id === action.payload) state.currentCampaign = null;
      })

      // Recipients
      .addCase(addRecipients.fulfilled, (state) => { state.loading = false; })
      .addCase(fetchRecipients.fulfilled, (state, action) => {
        state.loading = false;
        state.recipients = action.payload.recipients;
        state.recipientsTotal = action.payload.total;
      })

      // Verification
      .addCase(verifyEmails.pending, pending)
      .addCase(verifyEmails.fulfilled, (state, action) => { state.loading = false; state.verificationResults = action.payload; })
      .addCase(verifyEmails.rejected, rejected)
      .addCase(checkHistory.fulfilled, (state, action) => { state.historyResults = action.payload; })
      .addCase(checkDuplicates.fulfilled, (state, action) => { state.duplicateResults = action.payload; })
      .addCase(checkSpamScore.fulfilled, (state, action) => { state.spamScore = action.payload; })

      // Analytics
      .addCase(fetchAnalytics.fulfilled, (state, action) => { state.loading = false; state.analytics = action.payload; })
      .addCase(fetchLiveFeed.fulfilled, (state, action) => { state.liveFeed = action.payload; })
      .addCase(fetchActivityLog.fulfilled, (state, action) => { state.activityLog = action.payload.log; state.activityLogTotal = action.payload.total; })
      .addCase(fetchInsights.fulfilled, (state, action) => { state.insights = action.payload; })
      .addCase(compareCampaigns.fulfilled, (state, action) => { state.comparison = action.payload; })
      .addCase(fetchSendTimePrediction.fulfilled, (state, action) => { state.sendTimePrediction = action.payload; })

      // Segments
      .addCase(fetchSegments.fulfilled, (state, action) => { state.segments = action.payload.segments; state.segmentsTotal = action.payload.total; })
      .addCase(createSegment.fulfilled, (state, action) => { state.segments.unshift(action.payload); })
      .addCase(deleteSegment.fulfilled, (state, action) => { state.segments = state.segments.filter(s => s._id !== action.payload); })

      // Playbooks
      .addCase(fetchPlaybooks.fulfilled, (state, action) => { state.playbooks = action.payload.playbooks; state.playbooksTotal = action.payload.total; })
      .addCase(createPlaybook.fulfilled, (state, action) => { state.playbooks.unshift(action.payload); })
      .addCase(deletePlaybook.fulfilled, (state, action) => { state.playbooks = state.playbooks.filter(p => p._id !== action.payload); })

      // Domain Health + Send Limits
      .addCase(fetchDomainHealth.fulfilled, (state, action) => { state.domainHealth = action.payload; })
      .addCase(fetchSendLimits.fulfilled, (state, action) => { state.sendLimits = action.payload; })
      .addCase(updateSendLimit.fulfilled, (state, action) => {
        const idx = state.sendLimits.findIndex(l => l.userId === action.payload.userId);
        if (idx >= 0) state.sendLimits[idx] = action.payload; else state.sendLimits.push(action.payload);
      })

      // Warm-up
      .addCase(fetchWarmUpProjection.fulfilled, (state, action) => { state.warmUpProjection = action.payload; })
      .addCase(fetchLeadContactStats.fulfilled, (state, action) => { state.leadContactStats = action.payload; });
  },
});

export const {
  clearError, clearCurrentCampaign, setWizardStep, setWizardDraft,
  clearWizardDraft, clearVerificationResults, appendLiveFeedEvent, updateCampaignStatus,
} = bulkOutreachSlice.actions;

export default bulkOutreachSlice.reducer;
